import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  Search,
  Building2,
  Users,
  FileText,
  AlertTriangle,
  MapPin,
  Mail,
  Phone,
  Tag,
  Clock,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  Filter,
  Send,
  MessageSquare,
  RefreshCw,
  Download,
  HardHat,
  Smartphone,
  Layers,
  Pause,
  Play,
  X,
  CheckSquare,
  Square,
  Plus,
  Copy,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite, canAccessPage } from "@/lib/rbac";
import { formatDate, formatCurrency } from "@/lib/demo-data";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

type BulkAction = "pause" | "unpause" | "message" | null;

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  TRIAL: { label: "Essai", variant: "outline" },
  ACTIVE: { label: "Actif", variant: "default" },
  PAST_DUE: { label: "Impayé", variant: "destructive" },
  CANCELLED: { label: "Résilié", variant: "destructive" },
  PAUSED: { label: "Suspendu", variant: "secondary" },
};

const PORTFOLIO_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: any }> = {
  ACTIVE: { label: "Actif", variant: "default", icon: CheckCircle2 },
  READY: { label: "Prêt", variant: "secondary", icon: CheckCircle2 },
  TO_CONFIGURE: { label: "À configurer", variant: "outline", icon: AlertTriangle },
  DOCS_EXPIRED: { label: "Docs expirés", variant: "destructive", icon: Clock },
};

const PROJECT_STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  // OpsStatus values (used as opsStatus)
  NEW: { label: "Nouveau", variant: "outline" },
  IN_REVIEW: { label: "En revue", variant: "secondary" },
  REQUESTED_DOCS: { label: "Attente docs", variant: "secondary" },
  WRITING: { label: "Rédaction", variant: "default" },
  READY: { label: "Prêt", variant: "default" },
  SUBMITTED: { label: "Soumis", variant: "default" },
  WON: { label: "Gagné", variant: "default" },
  LOST: { label: "Perdu", variant: "destructive" },
  CANCELLED: { label: "Annulé", variant: "destructive" },
  // ResponseStatus fallbacks (used as status)
  WAITING_CLIENT_DOCS: { label: "Attente docs", variant: "secondary" },
  IN_PRODUCTION: { label: "En prod", variant: "default" },
  READY_FOR_REVIEW: { label: "Prêt revue", variant: "default" },
};

const DOC_CATEGORY_LABELS: Record<string, string> = {
  KBIS: "Kbis",
  ASSURANCE: "Assurance",
  URSSAF: "URSSAF",
  QUALIF: "Qualification",
  REFERENCE: "Référence",
  AUTRE: "Autre",
};

const LIFECYCLE_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  lead: { label: "Lead", variant: "outline" },
  trial: { label: "Essai", variant: "secondary" },
  client: { label: "Client", variant: "default" },
  churned: { label: "Churned", variant: "destructive" },
};

type PortfolioFilter = "all" | "with_projects" | "no_categories" | "expired_docs";
type ProductFilter = "all" | "travauxbtp" | "saas" | "mobile" | "architect";
type LifecycleFilter = "all" | "lead" | "trial" | "client" | "churned";

export default function Companies() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isReadOnly = !canWrite(user?.staffRole || null);
  const canSeeSupport = canAccessPage(user?.staffRole, "SUPPORT");
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [portfolioFilter, setPortfolioFilter] = useState<PortfolioFilter>("all");
  const [productFilter, setProductFilter] = useState<ProductFilter>("all");
  const [lifecycleFilter, setLifecycleFilter] = useState<LifecycleFilter>("all");
  const [companyNoteText, setCompanyNoteText] = useState("");

  // Read filter from URL on mount
  useEffect(() => {
    const f = searchParams.get("filter");
    if (f === "no_categories") setPortfolioFilter("no_categories");
    else if (f === "expired_docs") setPortfolioFilter("expired_docs");
    else if (f === "with_projects") setPortfolioFilter("with_projects");
  }, [searchParams]);

  const appFilterMap: Record<string, string> = {
    travauxbtp: "TRAVAUXBTP",
    saas: "SAAS",
    mobile: "MOBILE",
    architect: "ARCHITECT",
  };

  const { data, isLoading, isError, refetch } = trpc.admin.companies.list.useQuery({
    page,
    limit: 20,
    search: search || undefined,
    product: productFilter !== "all" && productFilter !== "architect" ? productFilter as any : undefined,
    app: productFilter !== "all" ? appFilterMap[productFilter] as any : undefined,
    lifecycle: lifecycleFilter !== "all" ? lifecycleFilter : undefined,
    withProjects: portfolioFilter === "with_projects" ? true : undefined,
    withoutCategories: portfolioFilter === "no_categories" ? true : undefined,
    withExpiredDocs: portfolioFilter === "expired_docs" ? true : undefined,
  });

  const { data: companyDetail, isLoading: companyDetailLoading, isError: companyDetailError, refetch: refetchCompanyDetail } = trpc.admin.companies.get.useQuery(
    { id: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const { data: companyNotes, isLoading: notesLoading, isError: notesError, refetch: refetchNotes } = trpc.admin.companies.notes.list.useQuery(
    { companyId: selectedCompanyId! },
    { enabled: !!selectedCompanyId }
  );

  const { data: companyTickets, isLoading: ticketsLoading, isError: ticketsError } = trpc.admin.support.list.useQuery(
    { companyId: selectedCompanyId!, open: true, page: 1, limit: 10 },
    { enabled: !!selectedCompanyId && canSeeSupport }
  );

  const addCompanyNoteMutation = trpc.admin.companies.notes.create.useMutation({
    onSuccess: () => {
      toast.success("Note ajoutée");
      setCompanyNoteText("");
      refetchNotes();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleAddCompanyNote = () => {
    if (!companyNoteText.trim() || !selectedCompanyId) return;
    addCompanyNoteMutation.mutate({
      companyId: selectedCompanyId,
      message: companyNoteText.trim(),
    });
  };

  const exportMutation = trpc.admin.companies.export.useMutation({
    onSuccess: (data: { filename: string; csv: string; count: number }) => {
      const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export téléchargé — ${data.count} entreprises`);
    },
    onError: () => toast.error("Erreur lors de l'export"),
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkAction>(null);
  const [bulkReason, setBulkReason] = useState("");
  const [bulkSubject, setBulkSubject] = useState("");
  const [bulkBody, setBulkBody] = useState("");

  const toggleSelect = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (!data?.items) return;
    const allIds = data.items.map((c: any) => c.id);
    setSelectedIds((prev) =>
      prev.size === allIds.length ? new Set() : new Set(allIds)
    );
  }, [data?.items]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  useEffect(() => {
    clearSelection();
  }, [page, search, portfolioFilter, productFilter, lifecycleFilter, clearSelection]);

  const selectedNames = data?.items
    ?.filter((c: any) => selectedIds.has(c.id))
    .map((c: any) => c.name) ?? [];

  const bulkPauseMutation = trpc.admin.companies.bulkPause.useMutation({
    onSuccess: (res) => {
      const failCount = res.failed?.length ?? 0;
      if (failCount === 0) toast.success(`${res.success} entreprise(s) pausée(s)`);
      else toast.warning(`${res.success} pausée(s), ${failCount} échec(s)`);
      clearSelection();
      setBulkAction(null);
      setBulkReason("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const bulkUnpauseMutation = trpc.admin.companies.bulkUnpause.useMutation({
    onSuccess: (res) => {
      const failCount = res.failed?.length ?? 0;
      if (failCount === 0) toast.success(`${res.success} entreprise(s) réactivée(s)`);
      else toast.warning(`${res.success} réactivée(s), ${failCount} échec(s)`);
      clearSelection();
      setBulkAction(null);
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const bulkMessageMutation = trpc.admin.companies.bulkSendMessage.useMutation({
    onSuccess: (res) => {
      const failCount = res.failed?.length ?? 0;
      if (failCount === 0) toast.success(`Message envoyé à ${res.success} entreprise(s)`);
      else toast.warning(`${res.success} envoyé(s), ${failCount} échec(s)`);
      clearSelection();
      setBulkAction(null);
      setBulkSubject("");
      setBulkBody("");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const handleBulkConfirm = () => {
    const ids = Array.from(selectedIds);
    if (bulkAction === "pause") {
      bulkPauseMutation.mutate({ companyIds: ids, reason: bulkReason });
    } else if (bulkAction === "unpause") {
      bulkUnpauseMutation.mutate({ companyIds: ids });
    } else if (bulkAction === "message") {
      bulkMessageMutation.mutate({ companyIds: ids, subject: bulkSubject, body: bulkBody });
    }
  };

  const isBulkPending = bulkPauseMutation.isPending || bulkUnpauseMutation.isPending || bulkMessageMutation.isPending;

  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";

  // ── Création d'un compte (AOP-8995.D → admin.companies.create) ──────────────
  // SUPER_ADMIN uniquement. Crée Company + owner ; le backend renvoie un lien
  // d'invitation (PasswordResetToken one-shot 30 min) que l'OPS transmet au
  // dirigeant — aucun mot de passe en clair n'existe.
  const [createDialog, setCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    companyName: "",
    ownerFirstName: "",
    ownerLastName: "",
    ownerEmail: "",
    phone: "",
    siret: "",
    plan: "DECOUVERTE" as "DECOUVERTE" | "ARTISAN" | "PRO_BTP" | "ENTERPRISE",
    status: "TRIAL" as "TRIAL" | "ACTIVE" | "PAST_DUE" | "PAUSED" | "CANCELLED",
    planExpiresAt: "",
    reason: "",
  });
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [createdCompanyId, setCreatedCompanyId] = useState<string | null>(null);

  function resetCreateForm() {
    setCreateForm({
      companyName: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",
      phone: "",
      siret: "",
      plan: "DECOUVERTE",
      status: "TRIAL",
      planExpiresAt: "",
      reason: "",
    });
  }

  const createMutation = trpc.admin.companies.create.useMutation({
    onSuccess: (res) => {
      toast.success(`Entreprise « ${res.company.name} » créée`);
      setInviteUrl(res.invite.url);
      setCreatedCompanyId(res.company.id);
      resetCreateForm();
      refetch();
    },
    onError: (err) => toast.error(err.message || "Erreur lors de la création"),
  });

  const isCreateFormValid =
    createForm.companyName.trim().length >= 1 &&
    createForm.ownerFirstName.trim().length >= 1 &&
    createForm.ownerLastName.trim().length >= 1 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(createForm.ownerEmail.trim()) &&
    createForm.reason.trim().length >= 20;

  function handleCreate() {
    if (!isCreateFormValid) return;
    createMutation.mutate({
      companyName: createForm.companyName.trim(),
      ownerFirstName: createForm.ownerFirstName.trim(),
      ownerLastName: createForm.ownerLastName.trim(),
      ownerEmail: createForm.ownerEmail.trim(),
      ...(createForm.phone.trim() ? { phone: createForm.phone.trim() } : {}),
      ...(createForm.siret.trim() ? { siret: createForm.siret.trim() } : {}),
      plan: createForm.plan,
      status: createForm.status,
      ...(createForm.planExpiresAt
        ? { planExpiresAt: new Date(createForm.planExpiresAt).toISOString() }
        : {}),
      reason: createForm.reason.trim(),
    });
  }

  function closeCreateDialog() {
    setCreateDialog(false);
    setInviteUrl(null);
    setCreatedCompanyId(null);
    resetCreateForm();
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold">Portefeuille Entreprises</h1>
            <p className="text-muted-foreground">
              {isReadOnly ? "Consulter les entreprises" : "Gestion et suivi du portefeuille client"}
            </p>
          </div>
          {isSuperAdmin && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportMutation.mutate({})}
                disabled={exportMutation.isPending}
              >
                <Download className="h-4 w-4 mr-2" />
                {exportMutation.isPending ? "Export…" : "Export CSV"}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setInviteUrl(null);
                  setCreatedCompanyId(null);
                  setCreateDialog(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer un compte
              </Button>
            </div>
          )}
        </div>

        {/* Création d'un compte — SUPER_ADMIN (AOP-8995.D) */}
        <Dialog open={createDialog} onOpenChange={(o) => (o ? setCreateDialog(true) : closeCreateDialog())}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Créer un compte entreprise
              </DialogTitle>
              <DialogDescription>
                Crée l'entreprise et son dirigeant (owner). Un lien d'invitation sécurisé
                est généré — aucun mot de passe n'est défini ici.
              </DialogDescription>
            </DialogHeader>

            {inviteUrl ? (
              <div className="space-y-4 py-2">
                <div className="rounded-md border border-green-500/40 bg-green-500/5 p-3 space-y-2">
                  <p className="text-sm font-medium text-green-700">
                    Compte créé. Transmettez ce lien d'invitation au dirigeant (valable 30 min) :
                  </p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={inviteUrl} className="font-mono text-xs" />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard?.writeText(inviteUrl);
                        toast.success("Lien copié");
                      }}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Le dirigeant définit son mot de passe via ce lien, puis se connecte
                    normalement.
                  </p>
                </div>
                <DialogFooter>
                  {createdCompanyId && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/companies/${createdCompanyId}`)}
                    >
                      Ouvrir la fiche
                    </Button>
                  )}
                  <Button onClick={closeCreateDialog}>Fermer</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Nom de l'entreprise *</label>
                  <Input
                    value={createForm.companyName}
                    onChange={(e) => setCreateForm((f) => ({ ...f, companyName: e.target.value }))}
                    placeholder="Ex : BTP Constructions SARL"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Prénom dirigeant *</label>
                    <Input
                      value={createForm.ownerFirstName}
                      onChange={(e) => setCreateForm((f) => ({ ...f, ownerFirstName: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Nom dirigeant *</label>
                    <Input
                      value={createForm.ownerLastName}
                      onChange={(e) => setCreateForm((f) => ({ ...f, ownerLastName: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Email dirigeant *</label>
                  <Input
                    type="email"
                    value={createForm.ownerEmail}
                    onChange={(e) => setCreateForm((f) => ({ ...f, ownerEmail: e.target.value }))}
                    placeholder="dirigeant@entreprise.fr"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Téléphone</label>
                    <Input
                      value={createForm.phone}
                      onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">SIRET</label>
                    <Input
                      value={createForm.siret}
                      onChange={(e) => setCreateForm((f) => ({ ...f, siret: e.target.value }))}
                      placeholder="14 chiffres"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Plan</label>
                    <Select
                      value={createForm.plan}
                      onValueChange={(v) => setCreateForm((f) => ({ ...f, plan: v as typeof f.plan }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DECOUVERTE">Découverte</SelectItem>
                        <SelectItem value="ARTISAN">Artisan</SelectItem>
                        <SelectItem value="PRO_BTP">Pro BTP</SelectItem>
                        <SelectItem value="ENTERPRISE">Entreprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium">Statut</label>
                    <Select
                      value={createForm.status}
                      onValueChange={(v) => setCreateForm((f) => ({ ...f, status: v as typeof f.status }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRIAL">Essai</SelectItem>
                        <SelectItem value="ACTIVE">Actif</SelectItem>
                        <SelectItem value="PAST_DUE">Impayé</SelectItem>
                        <SelectItem value="PAUSED">Suspendu</SelectItem>
                        <SelectItem value="CANCELLED">Résilié</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Fin d'essai / expiration du plan</label>
                  <Input
                    type="date"
                    value={createForm.planExpiresAt}
                    onChange={(e) => setCreateForm((f) => ({ ...f, planExpiresAt: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Vide + statut Essai → +14 jours par défaut.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">
                    Raison <span className="text-muted-foreground">(min. 20 caractères, audit)</span>
                  </label>
                  <Textarea
                    value={createForm.reason}
                    onChange={(e) => setCreateForm((f) => ({ ...f, reason: e.target.value }))}
                    placeholder="Ex : création manuelle suite à signature contrat hors funnel le 27/06…"
                    rows={2}
                  />
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={closeCreateDialog}>
                    Annuler
                  </Button>
                  <Button disabled={!isCreateFormValid || createMutation.isPending} onClick={handleCreate}>
                    {createMutation.isPending ? "Création…" : "Créer le compte"}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <Input
                    placeholder="Rechercher par nom, email, SIRET..."
                    aria-label="Rechercher une entreprise"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <Select
                  value={productFilter}
                  onValueChange={(v) => { setProductFilter(v as ProductFilter); setPage(1); }}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Produit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous produits</SelectItem>
                    <SelectItem value="travauxbtp">TravauxBTP</SelectItem>
                    <SelectItem value="saas">SaaS</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                    <SelectItem value="architect">Architect</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={lifecycleFilter}
                  onValueChange={(v) => { setLifecycleFilter(v as LifecycleFilter); setPage(1); }}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Lifecycle" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous cycles</SelectItem>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="trial">Essai</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="churned">Churned</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={portfolioFilter}
                  onValueChange={(v) => { setPortfolioFilter(v as PortfolioFilter); setPage(1); }}
                >
                  <SelectTrigger className="w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous statuts</SelectItem>
                    <SelectItem value="with_projects">Avec projets</SelectItem>
                    <SelectItem value="no_categories">Sans catégories</SelectItem>
                    <SelectItem value="expired_docs">Docs expirés</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Action Toolbar */}
        {selectedIds.size > 0 && !isReadOnly && (
          <div className="sticky top-0 z-30 flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur px-4 py-2.5 shadow-sm">
            <button type="button" onClick={toggleSelectAll} className="p-0.5">
              {data?.items && selectedIds.size === data.items.length
                ? <CheckSquare className="h-4 w-4 text-primary" />
                : <Square className="h-4 w-4 text-muted-foreground" />}
            </button>
            <span className="text-sm font-medium">{selectedIds.size} sélectionné(s)</span>
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => setBulkAction("pause")}>
              <Pause className="h-3.5 w-3.5 mr-1" />Pauser
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkAction("unpause")}>
              <Play className="h-3.5 w-3.5 mr-1" />Reprendre
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkAction("message")}>
              <Send className="h-3.5 w-3.5 mr-1" />Envoyer message
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {/* Companies Grid */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-muted-foreground">
              {data?.total || 0} entreprise(s) trouvée(s)
            </p>
            {!isReadOnly && data?.items && data.items.length > 0 && (
              <button
                type="button"
                onClick={toggleSelectAll}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
              >
                {selectedIds.size === data.items.length ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                {selectedIds.size === data.items.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
            )}
          </div>

          {isLoading ? (
            <ListPageSkeleton rows={6} />
          ) : isError ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
              <AlertCircle className="h-12 w-12 text-destructive/40" />
              <p className="text-muted-foreground font-medium">Erreur de chargement des entreprises</p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />Réessayer
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {data?.items.map((company: any) => {
                const portfolioCfg = PORTFOLIO_STATUS_CONFIG[company.portfolioStatus] || PORTFOLIO_STATUS_CONFIG.READY;
                const PortfolioIcon = portfolioCfg.icon;

                return (
                  <Card
                    key={company.id}
                    className={`cursor-pointer hover:shadow-md hover:border-primary/20 transition-all duration-200 ${selectedIds.has(company.id) ? "ring-2 ring-primary/40 border-primary/30" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/companies/${company.id}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/companies/${company.id}`); } }}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={(e) => toggleSelect(company.id, e)}
                            className="shrink-0 p-0.5 rounded hover:bg-muted/50"
                          >
                            {selectedIds.has(company.id)
                              ? <CheckSquare className="h-5 w-5 text-primary" />
                              : <Square className="h-5 w-5 text-muted-foreground/40 hover:text-muted-foreground" />}
                          </button>
                        )}
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base truncate">{company.name}</CardTitle>
                          {company.email && (
                            <p className="text-xs text-muted-foreground truncate">{company.email}</p>
                          )}
                        </div>
                        <Badge variant={STATUS_CONFIG[company.status]?.variant || "outline"}>
                          {STATUS_CONFIG[company.status]?.label || company.status || "N/A"}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {/* Product badges */}
                      <div className="flex flex-wrap gap-1 mb-2">
                        {company.products?.travauxbtp && (
                          <Badge className="text-xs bg-orange-500/10 text-orange-600 border-orange-200 hover:bg-orange-500/20">
                            <HardHat className="h-3 w-3 mr-1" />
                            TravauxBTP
                          </Badge>
                        )}
                        {company.products?.saas && (
                          <Badge className="text-xs bg-blue-500/10 text-blue-600 border-blue-200 hover:bg-blue-500/20">
                            <Layers className="h-3 w-3 mr-1" />
                            SaaS{company.plan ? ` ${company.plan}` : ""}
                          </Badge>
                        )}
                        {company.products?.mobile && (
                          <Badge className="text-xs bg-purple-500/10 text-purple-600 border-purple-200 hover:bg-purple-500/20">
                            <Smartphone className="h-3 w-3 mr-1" />
                            Mobile
                          </Badge>
                        )}
                        {!company.products?.travauxbtp && !company.products?.saas && !company.products?.mobile && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Freemium
                          </Badge>
                        )}
                        {company.lifecycle && (
                          <Badge variant={LIFECYCLE_CONFIG[company.lifecycle]?.variant || "outline"} className="text-xs ml-auto">
                            {LIFECYCLE_CONFIG[company.lifecycle]?.label || company.lifecycle}
                          </Badge>
                        )}
                      </div>
                      {/* Active app accesses */}
                      {company.activeApps && company.activeApps.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {(company.activeApps as string[]).map((app: string) => (
                            <Badge key={app} variant="outline" className="text-[10px] px-1.5 py-0">
                              {app}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant={portfolioCfg.variant} className="text-xs">
                          <PortfolioIcon className="h-3 w-3 mr-1" />
                          {portfolioCfg.label}
                        </Badge>
                        {company.expiredDocsCount > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            {company.expiredDocsCount} doc(s) expiré(s)
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs mt-3">
                        <div>
                          <div className="font-bold text-lg">{company._count?.ResponseProject || 0}</div>
                          <span className="text-muted-foreground">Projets</span>
                        </div>
                        <div>
                          <div className="font-bold text-lg">{company._count?.TenderMatch || 0}</div>
                          <span className="text-muted-foreground">Matchs</span>
                        </div>
                        <div>
                          <div className="font-bold text-lg">{company._count?.CompanyDocument || 0}</div>
                          <span className="text-muted-foreground">Docs</span>
                        </div>
                      </div>
                      {!company.hasCategories && (
                        <div className="mt-2 text-xs text-warning flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Aucune catégorie configurée
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {data && data.items.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">Aucune entreprise trouvée</p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {data && data.total > data.limit && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-muted-foreground">
                Page {data.page} / {Math.ceil(data.total / data.limit)}
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
                <Button size="sm" variant="outline" disabled={page >= Math.ceil(data.total / data.limit)} onClick={() => setPage(p => p + 1)}>Suivant</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bulk Action Confirmation Dialog */}
      <Dialog open={bulkAction !== null} onOpenChange={(open) => { if (!open) { setBulkAction(null); setBulkReason(""); setBulkSubject(""); setBulkBody(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "pause" && `Pauser ${selectedIds.size} entreprise(s) ?`}
              {bulkAction === "unpause" && `Réactiver ${selectedIds.size} entreprise(s) ?`}
              {bulkAction === "message" && `Envoyer un message à ${selectedIds.size} entreprise(s) ?`}
            </DialogTitle>
            <DialogDescription>
              {selectedNames.slice(0, 5).join(", ")}
              {selectedNames.length > 5 && ` et ${selectedNames.length - 5} autre(s)`}
            </DialogDescription>
          </DialogHeader>

          {bulkAction === "pause" && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Raison (obligatoire)</label>
              <textarea
                className="w-full min-h-[80px] px-3 py-2 border rounded-md text-sm"
                placeholder="Raison de la pause..."
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
              />
            </div>
          )}

          {bulkAction === "message" && (
            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Sujet</label>
                <Input
                  placeholder="Sujet du message..."
                  value={bulkSubject}
                  onChange={(e) => setBulkSubject(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Message</label>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 border rounded-md text-sm"
                  placeholder="Contenu du message..."
                  value={bulkBody}
                  onChange={(e) => setBulkBody(e.target.value)}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAction(null)} disabled={isBulkPending}>
              Annuler
            </Button>
            <Button
              onClick={handleBulkConfirm}
              disabled={
                isBulkPending ||
                (bulkAction === "pause" && !bulkReason.trim()) ||
                (bulkAction === "message" && (!bulkSubject.trim() || !bulkBody.trim()))
              }
              variant={bulkAction === "pause" ? "destructive" : "default"}
            >
              {isBulkPending ? "En cours..." : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedCompanyId} onOpenChange={(open) => !open && setSelectedCompanyId(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Building2 className="h-5 w-5" />
              {companyDetail?.name || "Chargement..."}
            </DialogTitle>
            <DialogDescription>
              Fiche entreprise — portefeuille OPS
            </DialogDescription>
          </DialogHeader>

          {companyDetailLoading ? (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="text-center p-3 border rounded-lg">
                    <Skeleton className="h-8 w-16 mx-auto mb-2" />
                    <Skeleton className="h-3 w-12 mx-auto" />
                  </div>
                ))}
              </div>
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          ) : companyDetail ? (
            <Tabs defaultValue="overview" className="mt-4">
              <TabsList className={`grid w-full ${canSeeSupport ? "grid-cols-6" : "grid-cols-5"}`}>
                <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
                <TabsTrigger value="projects">
                  Projets ({(companyDetail as any).ResponseProject?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="documents">
                  Documents ({(companyDetail as any).CompanyDocument?.length || 0})
                </TabsTrigger>
                {canSeeSupport && (
                  <TabsTrigger value="support">
                    Support ({(companyDetail as any).openTicketsCount || 0})
                  </TabsTrigger>
                )}
                <TabsTrigger value="notes">
                  Notes ({companyNotes?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="users">
                  Utilisateurs ({companyDetail.User?.length || 0})
                </TabsTrigger>
              </TabsList>

              {/* Overview Tab — Key info + billing + stats */}
              <TabsContent value="overview" className="space-y-4">
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-2xl font-bold">{(companyDetail._count as any)?.ResponseProject || 0}</div>
                    <p className="text-xs text-muted-foreground">Projets</p>
                  </div>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-2xl font-bold">{(companyDetail as any).projectStats?.winRate || 0}%</div>
                    <p className="text-xs text-muted-foreground">
                      Taux gain ({(companyDetail as any).projectStats?.won || 0}W / {(companyDetail as any).projectStats?.lost || 0}L)
                    </p>
                  </div>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency((companyDetail as any).billing?.totalRevenue || 0)}
                    </div>
                    <p className="text-xs text-muted-foreground">CA total</p>
                  </div>
                  <div className="text-center p-3 border rounded-lg">
                    <div className="text-2xl font-bold">{(companyDetail._count as any)?.TenderMatch || 0}</div>
                    <p className="text-xs text-muted-foreground">Matchs AO</p>
                  </div>
                </div>

                {/* Billing breakdown */}
                {(companyDetail as any).billing && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      Facturation
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-muted-foreground">Service</p>
                        <p className="font-medium">{formatCurrency((companyDetail as any).billing.serviceRevenue)}</p>
                        <p className="text-xs text-muted-foreground">{(companyDetail as any).billing.serviceInvoiceCount} facture(s)</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Assistance</p>
                        <p className="font-medium">{formatCurrency((companyDetail as any).billing.assistanceRevenue)}</p>
                        <p className="text-xs text-muted-foreground">{(companyDetail as any).billing.assistanceCount} commande(s)</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Success fee</p>
                        <p className="font-medium">{formatCurrency((companyDetail as any).billing.successFeeRevenue)}</p>
                        <p className="text-xs text-muted-foreground">{(companyDetail as any).billing.successFeeCount} fee(s)</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Company info */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <h4 className="font-semibold">Coordonnées</h4>
                    <div className="space-y-2 text-sm">
                      {companyDetail.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span>{companyDetail.email}</span>
                        </div>
                      )}
                      {(companyDetail as any).phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <span>{(companyDetail as any).phone}</span>
                        </div>
                      )}
                      {(companyDetail as any).address && (
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{(companyDetail as any).address}</span>
                        </div>
                      )}
                      {companyDetail.siret && (
                        <div className="text-muted-foreground">SIRET: {companyDetail.siret}</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-semibold">Statut & Plan</h4>
                    <div className="space-y-2">
                      <Badge variant={STATUS_CONFIG[(companyDetail as any).status]?.variant || "outline"}>
                        {STATUS_CONFIG[(companyDetail as any).status]?.label || (companyDetail as any).status}
                      </Badge>
                      {(companyDetail as any).plan && (
                        <p className="text-sm text-muted-foreground">Plan: {(companyDetail as any).plan}</p>
                      )}
                      <p className="text-sm text-muted-foreground">
                        Créé le {formatDate(companyDetail.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Profile: Zones + Categories */}
                {(companyDetail as any).CompanyProfile && (
                  <div className="space-y-3 border-t pt-4">
                    <h4 className="font-semibold">Profil de matching</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium mb-1">Zones d'intervention</p>
                        <div className="flex flex-wrap gap-1">
                          {(companyDetail as any).CompanyProfile.zones?.length > 0
                            ? (companyDetail as any).CompanyProfile.zones.map((z: string) => (
                                <Badge key={z} variant="outline" className="text-xs">
                                  <MapPin className="h-3 w-3 mr-1" />
                                  {z}
                                </Badge>
                              ))
                            : <span className="text-sm text-warning flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" /> Non renseigné
                              </span>
                          }
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium mb-1">Catégories</p>
                        <div className="flex flex-wrap gap-1">
                          {(companyDetail as any).CompanyProfile.CompanyProfileCategory?.length > 0
                            ? (companyDetail as any).CompanyProfile.CompanyProfileCategory.map((pc: any) => (
                                <Badge key={pc.id} variant="secondary" className="text-xs">
                                  <Tag className="h-3 w-3 mr-1" />
                                  {pc.CompanyCategory?.label || pc.categoryId}
                                </Badge>
                              ))
                            : <span className="text-sm text-warning flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3" /> Aucune catégorie
                              </span>
                          }
                        </div>
                      </div>
                    </div>
                    {(companyDetail as any).CompanyProfile.trades?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium mb-1">Métiers</p>
                        <div className="flex flex-wrap gap-1">
                          {(companyDetail as any).CompanyProfile.trades.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!(companyDetail as any).CompanyProfile && (
                  <div className="border border-warning/30 rounded-lg p-4 bg-warning/5">
                    <div className="flex items-center gap-2 text-warning">
                      <AlertTriangle className="h-4 w-4" />
                      <p className="font-medium text-sm">Profil non configuré</p>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cette entreprise n'a pas de profil de matching. Elle ne recevra pas d'AO pertinents.
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Projects Tab */}
              <TabsContent value="projects">
                {(companyDetail as any).ResponseProject && (companyDetail as any).ResponseProject.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>AO</TableHead>
                        <TableHead>Statut</TableHead>
                        <TableHead>Deadline</TableHead>
                        <TableHead>Partenaire</TableHead>
                        <TableHead>Créé le</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(companyDetail as any).ResponseProject.map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium max-w-xs truncate">
                            {p.Tender?.title || "-"}
                          </TableCell>
                          <TableCell>
                            <Badge variant={PROJECT_STATUS_CONFIG[p.opsStatus || p.status]?.variant || "outline"}>
                              {PROJECT_STATUS_CONFIG[p.opsStatus || p.status]?.label || p.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {p.Tender?.deadlineAt ? formatDate(p.Tender.deadlineAt) : "-"}
                          </TableCell>
                          <TableCell>
                            {p.PartnerAssignment?.Partner?.name || (
                              <span className="text-warning text-xs">Non assigné</span>
                            )}
                          </TableCell>
                          <TableCell>{formatDate(p.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-muted-foreground">Aucun projet</p>
                  </div>
                )}
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents">
                {(companyDetail as any).CompanyDocument && (companyDetail as any).CompanyDocument.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Catégorie</TableHead>
                        <TableHead>Expiration</TableHead>
                        <TableHead>Ajouté le</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(companyDetail as any).CompanyDocument.map((doc: any) => {
                        const isExpired = doc.expiresAt && new Date(doc.expiresAt) < new Date();
                        const isExpiringSoon =
                          doc.expiresAt &&
                          !isExpired &&
                          new Date(doc.expiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                        return (
                          <TableRow key={doc.id}>
                            <TableCell className="font-medium">
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline"
                              >
                                {doc.name}
                              </a>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {DOC_CATEGORY_LABELS[doc.category] || doc.category}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {doc.expiresAt ? (
                                <span className={isExpired ? "text-destructive font-medium" : isExpiringSoon ? "text-warning" : ""}>
                                  {isExpired && <Clock className="h-3 w-3 inline mr-1" />}
                                  {formatDate(doc.expiresAt)}
                                  {isExpired && " (expiré)"}
                                  {isExpiringSoon && " (bientôt)"}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>{formatDate(doc.createdAt)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-muted-foreground">Aucun document</p>
                  </div>
                )}
              </TabsContent>

              {/* Support Tab */}
              {canSeeSupport && (
                <TabsContent value="support">
                  {ticketsLoading ? (
                    <div className="space-y-2 py-4">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="h-10 w-full" />
                      ))}
                    </div>
                  ) : ticketsError ? (
                    <div className="flex items-center gap-2 text-sm text-destructive py-4">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      <span>Erreur de chargement des tickets</span>
                    </div>
                  ) : companyTickets && companyTickets.items.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sujet</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Priorité</TableHead>
                          <TableHead>Statut</TableHead>
                          <TableHead>Assigné à</TableHead>
                          <TableHead>Créé le</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {companyTickets.items.map((ticket: any) => (
                          <TableRow key={ticket.id}>
                            <TableCell className="font-medium max-w-xs truncate">
                              {ticket.subject}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{ticket.type}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={ticket.priority === "URGENT" ? "destructive" : ticket.priority === "HIGH" ? "destructive" : "secondary"}
                                className="text-xs"
                              >
                                {ticket.priority}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{ticket.status}</Badge>
                            </TableCell>
                            <TableCell className="text-xs">
                              {ticket.Assignee
                                ? [ticket.Assignee.firstName, ticket.Assignee.lastName].filter(Boolean).join(" ") || ticket.Assignee.email
                                : <span className="text-warning">Non assigné</span>}
                            </TableCell>
                            <TableCell className="text-xs">{formatDate(ticket.createdAt)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8">
                      <CheckCircle2 className="h-8 w-8 mx-auto text-success mb-2" />
                      <p className="text-muted-foreground">Aucun ticket ouvert</p>
                    </div>
                  )}
                </TabsContent>
              )}

              {/* Notes Tab */}
              <TabsContent value="notes" className="space-y-4">
                <div className="flex gap-2">
                  <textarea
                    className="flex-1 min-h-[80px] px-3 py-2 border rounded-md text-sm"
                    placeholder="Ajouter une note interne sur cette entreprise..."
                    aria-label="Note interne sur l'entreprise"
                    value={companyNoteText}
                    onChange={(e) => setCompanyNoteText(e.target.value)}
                  />
                  <Button
                    size="sm"
                    onClick={handleAddCompanyNote}
                    disabled={!companyNoteText.trim() || addCompanyNoteMutation.isPending}
                    className="self-end"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Envoyer
                  </Button>
                </div>

                {notesLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : notesError ? (
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4 shrink-0" />
                    <span>Erreur de chargement des notes</span>
                  </div>
                ) : companyNotes && companyNotes.length > 0 ? (
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {companyNotes.map((note: any) => (
                      <div key={note.id} className="p-3 border rounded-lg border-l-4 border-l-primary">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">
                            {note.User
                              ? [note.User.firstName, note.User.lastName].filter(Boolean).join(" ") || note.User.email
                              : "Système"}
                          </span>
                          <span className="text-xs text-muted-foreground">{formatDate(note.createdAt)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.afterJson?.message || ""}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">Aucune note</p>
                    <p className="text-sm text-muted-foreground">Ajoutez une note interne ci-dessus</p>
                  </div>
                )}
              </TabsContent>

              {/* Users Tab */}
              <TabsContent value="users">
                {companyDetail.User && companyDetail.User.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Nom</TableHead>
                        <TableHead>Rôle</TableHead>
                        <TableHead>Inscrit le</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {companyDetail.User.map((u: any) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-medium">{u.email}</TableCell>
                          <TableCell>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{u.role}</Badge>
                          </TableCell>
                          <TableCell>{formatDate(u.createdAt)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8">
                    <Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                    <p className="text-muted-foreground">Aucun utilisateur</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : companyDetailError ? (
            <div className="flex flex-col items-center justify-center min-h-[200px] gap-4 mt-4">
              <AlertCircle className="h-12 w-12 text-destructive/40" />
              <p className="text-muted-foreground font-medium">Erreur de chargement de l'entreprise</p>
              <Button variant="outline" onClick={() => refetchCompanyDetail()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
